import React, { createContext, useContext, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { translations } from './translations'
import { 
  getLanguageFromPathname, 
  getEquivalentPath, 
  getPageUrl, 
  getServiceUrl, 
  getLocalizedServiceSlug, 
  getCanonicalServiceKey 
} from './routes'

const LanguageContext = createContext()

export function LanguageProvider({ children }) {
  const location = useLocation()
  const navigate = useNavigate()

  // Language is strictly derived from the current URL
  const lang = getLanguageFromPathname(location.pathname)

  // Switch to target language by navigating to equivalent localized URL
  const setLang = (targetLang) => {
    if (targetLang === lang) return
    const nextPath = getEquivalentPath(location.pathname, targetLang)
    // Preserve search params if any
    const search = location.search || ''
    navigate(`${nextPath}${search}`)
  }

  const toggleLang = () => {
    setLang(lang === 'da' ? 'en' : 'da')
  }

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang
    }
  }, [lang])

  const t = translations[lang] || translations.da

  const value = {
    lang,
    setLang,
    toggleLang,
    t,
    isEn: lang === 'en',
    isDa: lang === 'da',
    // Helpers for linking inside components
    getPageUrl: (pageKey) => getPageUrl(pageKey, lang),
    getServiceUrl: (serviceKey) => getServiceUrl(serviceKey, lang),
    getLocalizedServiceSlug: (serviceKey) => getLocalizedServiceSlug(serviceKey, lang),
    getCanonicalServiceKey,
    getEquivalentPath: (targetLang) => getEquivalentPath(location.pathname, targetLang),
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
